import { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Box,
  Typography,
  FormControlLabel,
  Checkbox,
  IconButton,
  CircularProgress,
  Paper,
} from '@mui/material';
import {
  Download as DownloadIcon,
  Upload as UploadIcon,
  CameraAlt as CameraAltIcon,
  Delete as DeleteIcon,
  PictureAsPdf as PdfIcon,
} from '@mui/icons-material';
import { useAtom } from 'jotai';
import { userAtom } from '../../state/auth';
import { useFeedback, MAX_FILE_BYTES } from '../../context/FeedbackContext';
import { useLocation } from 'react-router-dom';
import { useNotification } from '../../hooks/useNotification';
import { apiCall } from '../../services/api';
import RedactionModal from './RedactionModal';

interface FeedbackModalProps {
  open: boolean;
  onClose: () => void;
}

interface FormData {
  type: 'bug' | 'feature' | '';
  summary: string;
  severity: 'critical' | 'high' | 'medium' | 'low' | '';
  reproductionSteps: string;
  expectedBehavior: string;
  actualBehavior: string;
  useCase: string;
  priority: 'high' | 'medium' | 'low' | '';
}

const ACCEPTED_MIME = 'image/*,application/pdf';

const formatBytes = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};

const readFileAsDataUrl = (file: File | Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error ?? new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });

export default function FeedbackModal({ open, onClose }: FeedbackModalProps) {
  const [user] = useAtom(userAtom);
  const {
    attachments,
    attachmentError,
    addAttachment,
    removeAttachment,
    updateAttachment,
    showBanner,
    isBannerVisible,
    isCapturing,
  } = useFeedback();
  const location = useLocation();
  const { showSuccess, showError } = useNotification();

  const [formData, setFormData] = useState<FormData>({
    type: '',
    summary: '',
    severity: '',
    reproductionSteps: '',
    expectedBehavior: '',
    actualBehavior: '',
    useCase: '',
    priority: '',
  });

  const [includeNetworkLogs, setIncludeNetworkLogs] = useState(true);
  const [redactTargetId, setRedactTargetId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Partial<Record<keyof FormData, string>>>({});
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open && !isBannerVisible && !isCapturing) {
      // Only reset form when modal truly closes (not when showing banner or capturing)
      setFormData({
        type: '',
        summary: '',
        severity: '',
        reproductionSteps: '',
        expectedBehavior: '',
        actualBehavior: '',
        useCase: '',
        priority: '',
      });
      setIncludeNetworkLogs(true);
      setRedactTargetId(null);
      setErrors({});
    }
  }, [open, isBannerVisible, isCapturing]);

  const handleChange = (field: keyof FormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const handleRedactionSave = (redactedImage: string) => {
    if (redactTargetId) {
      updateAttachment(redactTargetId, redactedImage);
    }
  };

  const addFile = async (file: File | Blob, fallbackName: string) => {
    const contentType = file.type || 'application/octet-stream';
    const isImage = contentType.startsWith('image/');
    const isPdf = contentType === 'application/pdf';

    if (!isImage && !isPdf) {
      showError('Only images and PDF files can be attached.');
      return;
    }

    // Fast size check before decoding (the context enforces the authoritative cap too).
    if (file.size > MAX_FILE_BYTES) {
      showError(
        `"${(file as File).name || fallbackName}" is larger than ${Math.round(
          MAX_FILE_BYTES / 1024 / 1024
        )} MB.`
      );
      return;
    }

    try {
      const dataUrl = await readFileAsDataUrl(file);
      addAttachment({
        fileName: (file as File).name || fallbackName,
        contentType,
        dataUrl,
        kind: isPdf ? 'pdf' : 'image',
      });
    } catch {
      showError('Failed to read the selected file.');
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    // Reset input first so the same file can be re-selected later.
    e.target.value = '';
    for (let i = 0; i < files.length; i++) {
      await addFile(files[i], `attachment-${i + 1}`);
    }
  };

  const handlePaste = async (e: React.ClipboardEvent<HTMLDivElement>) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    const imageFiles: File[] = [];
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.kind === 'file' && item.type.startsWith('image/')) {
        const file = item.getAsFile();
        if (file) imageFiles.push(file);
      }
    }
    if (imageFiles.length === 0) return;
    e.preventDefault();
    for (let i = 0; i < imageFiles.length; i++) {
      const ext = imageFiles[i].type.split('/')[1] || 'png';
      await addFile(imageFiles[i], `pasted-image-${attachments.length + i + 1}.${ext}`);
    }
  };

  const collectBrowserInfo = () => {
    return {
      userAgent: navigator.userAgent,
      screenResolution: `${window.screen.width}x${window.screen.height}`,
      viewportSize: `${window.innerWidth}x${window.innerHeight}`,
      platform: navigator.platform,
      language: navigator.language,
    };
  };

  const collectNetworkLogs = () => {
    const logs: unknown[] = [];

    // Collect performance entries for network requests
    try {
      const entries = performance.getEntriesByType('resource');
      const recentEntries = entries.slice(-25).map((entry) => ({
        type: 'network',
        name: entry.name,
        duration: entry.duration,
        initiatorType: (entry as PerformanceResourceTiming).initiatorType,
        timestamp: entry.startTime,
      }));
      logs.push(...recentEntries);
    } catch (error) {
      console.error('Failed to collect network logs:', error);
    }

    // Collect console logs from global tracking (if available)
    try {
      if ((window as any).__consoleLog && Array.isArray((window as any).__consoleLog)) {
        const recentConsoleLogs = (window as any).__consoleLog.slice(-50).map((log: any) => ({
          type: 'console',
          level: log.level,
          message: log.message,
          timestamp: log.timestamp,
        }));
        logs.push(...recentConsoleLogs);
      }
    } catch (error) {
      console.error('Failed to collect console logs:', error);
    }

    return logs.slice(-100);
  };

  const sanitizeData = (data: unknown) => {
    // Remove sensitive tokens, keys, and passwords
    const sensitivePatterns = [
      /Bearer\s+[A-Za-z0-9\-._~+/]+=*/gi,
      /api[_-]?key["\s:=]+[A-Za-z0-9]+/gi,
      /password["\s:=]+[^\s,}]+/gi,
      /token["\s:=]+[A-Za-z0-9\-._~+/]+=*/gi,
    ];

    let sanitized = JSON.stringify(data);
    sensitivePatterns.forEach((pattern) => {
      sanitized = sanitized.replace(pattern, '[REDACTED]');
    });

    return JSON.parse(sanitized);
  };

  const isFormValid =
    !!formData.type &&
    formData.summary.length >= 10 &&
    (formData.type !== 'bug' || !!formData.severity) &&
    (formData.type !== 'feature' || !!formData.priority);

  const validate = () => {
    const newErrors: Partial<Record<keyof FormData, string>> = {};

    if (!formData.type) {
      newErrors.type = 'Please select feedback type';
    }

    if (!formData.summary || formData.summary.length < 10) {
      newErrors.summary = 'Summary must be at least 10 characters';
    }

    if (formData.type === 'bug' && !formData.severity) {
      newErrors.severity = 'Please select bug severity';
    }

    if (formData.type === 'feature' && !formData.priority) {
      newErrors.priority = 'Please select priority';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    setLoading(true);

    try {
      const payload: any = {
        feedbackType: formData.type,
        summary: formData.summary,
        user: {
          name: user?.name || 'Unknown User',
          email: user?.email || '',
          role: user?.role || 'unknown',
        },
        page: {
          path: location.pathname,
          url: window.location.href,
          fullPath: `${location.pathname}${location.search}`,
        },
        timestamp: new Date().toISOString(),
        browser: collectBrowserInfo(),
      };

      // Add type-specific fields
      if (formData.type === 'bug') {
        payload.bug = {
          severity: formData.severity,
          reproductionSteps: formData.reproductionSteps,
          expectedBehavior: formData.expectedBehavior,
          actualBehavior: formData.actualBehavior,
        };
      } else if (formData.type === 'feature') {
        payload.feature = {
          useCase: formData.useCase,
          priority: formData.priority,
        };
      }

      // Attachments (screenshots + uploaded PDFs/images) as base64 for the
      // Lopie Tasks feedback API: [{ fileName, contentType, contentBase64 }]
      if (attachments.length > 0) {
        payload.attachments = attachments.map((a) => ({
          fileName: a.fileName,
          contentType: a.contentType,
          contentBase64: a.dataUrl.slice(a.dataUrl.indexOf(',') + 1),
        }));
      }

      if (includeNetworkLogs) {
        const logs = collectNetworkLogs();
        payload.networkLogs = sanitizeData(logs);
      }

      // Send to our Lambda function
      await apiCall('submit-feedback', {
        method: 'POST',
        body: payload,
      });

      showSuccess('Feedback submitted. Thank you.');

      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (error) {
      console.error('Failed to submit feedback:', error);
      showError(`Failed to submit feedback: ${(error as Error).message}`);
    } finally {
      setLoading(false);
    }
  };

  const redactTarget = attachments.find((a) => a.id === redactTargetId) ?? null;

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="h6">Submit Feedback</Typography>
          </Box>
        </DialogTitle>

        <DialogContent dividers onPaste={handlePaste}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            {/* Type Selection */}
            <FormControl fullWidth error={!!errors.type} size="small">
              <InputLabel>Feedback Type *</InputLabel>
              <Select
                value={formData.type}
                onChange={(e) => handleChange('type', e.target.value)}
                label="Feedback Type *"
              >
                <MenuItem value="bug">Bug Report</MenuItem>
                <MenuItem value="feature">Feature Request</MenuItem>
              </Select>
              {errors.type && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                  {errors.type}
                </Typography>
              )}
            </FormControl>

            {/* Summary */}
            <TextField
              label="Summary *"
              fullWidth
              multiline
              rows={2}
              value={formData.summary}
              onChange={(e) => handleChange('summary', e.target.value)}
              error={!!errors.summary}
              helperText={errors.summary || 'Minimum 10 characters'}
            />

            {/* Bug-specific fields */}
            {formData.type === 'bug' && (
              <>
                <FormControl fullWidth error={!!errors.severity}>
                  <InputLabel>Severity *</InputLabel>
                  <Select
                    value={formData.severity}
                    onChange={(e) => handleChange('severity', e.target.value)}
                    label="Severity *"
                  >
                    <MenuItem value="critical">Critical</MenuItem>
                    <MenuItem value="high">High</MenuItem>
                    <MenuItem value="medium">Medium</MenuItem>
                    <MenuItem value="low">Low</MenuItem>
                  </Select>
                  {errors.severity && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                      {errors.severity}
                    </Typography>
                  )}
                </FormControl>

                <TextField
                  label="Reproduction Steps"
                  fullWidth
                  multiline
                  rows={3}
                  value={formData.reproductionSteps}
                  onChange={(e) => handleChange('reproductionSteps', e.target.value)}
                  placeholder={'1. Go to...\n2. Click on...\n3. See error...'}
                />

                <TextField
                  label="Expected Behavior"
                  fullWidth
                  multiline
                  rows={2}
                  value={formData.expectedBehavior}
                  onChange={(e) => handleChange('expectedBehavior', e.target.value)}
                  placeholder="What should happen?"
                />

                <TextField
                  label="Actual Behavior"
                  fullWidth
                  multiline
                  rows={2}
                  value={formData.actualBehavior}
                  onChange={(e) => handleChange('actualBehavior', e.target.value)}
                  placeholder="What actually happens?"
                />
              </>
            )}

            {/* Feature Request fields */}
            {formData.type === 'feature' && (
              <>
                <TextField
                  label="Use Case / Reason"
                  fullWidth
                  multiline
                  rows={3}
                  value={formData.useCase}
                  onChange={(e) => handleChange('useCase', e.target.value)}
                  placeholder="Why do you need this feature? How would you use it?"
                />

                <FormControl fullWidth error={!!errors.priority}>
                  <InputLabel>Priority *</InputLabel>
                  <Select
                    value={formData.priority}
                    onChange={(e) => handleChange('priority', e.target.value)}
                    label="Priority *"
                  >
                    <MenuItem value="high">High</MenuItem>
                    <MenuItem value="medium">Medium</MenuItem>
                    <MenuItem value="low">Low</MenuItem>
                  </Select>
                  {errors.priority && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                      {errors.priority}
                    </Typography>
                  )}
                </FormControl>
              </>
            )}

            {/* Attachments Section */}
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                Attachments
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Add screenshots or PDF documents. Capture the screen, upload files, or paste an image
                (Ctrl/Cmd+V). Up to {Math.round(MAX_FILE_BYTES / 1024 / 1024)} MB each.
              </Typography>

              <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<CameraAltIcon />}
                  onClick={showBanner}
                >
                  Capture screen
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<UploadIcon />}
                  onClick={() => fileInputRef.current?.click()}
                >
                  Upload files
                </Button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept={ACCEPTED_MIME}
                  hidden
                  multiple
                  onChange={handleFileUpload}
                />
              </Box>

              {attachmentError && (
                <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
                  {attachmentError}
                </Typography>
              )}

              {attachments.length > 0 && (
                <Box sx={{ mt: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {attachments.map((att) => (
                    <Paper key={att.id} variant="outlined" sx={{ p: 1, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Box
                        sx={{
                          width: 48,
                          height: 48,
                          flexShrink: 0,
                          borderRadius: 1,
                          border: '1px solid',
                          borderColor: 'divider',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          overflow: 'hidden',
                          bgcolor: 'grey.50',
                        }}
                      >
                        {att.kind === 'image' ? (
                          <img
                            src={att.dataUrl}
                            alt={att.fileName}
                            style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'cover' }}
                          />
                        ) : (
                          <PdfIcon color="error" />
                        )}
                      </Box>

                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" noWrap title={att.fileName}>
                          {att.fileName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatBytes(att.size)}
                        </Typography>
                      </Box>

                      {att.kind === 'image' && (
                        <Button size="small" variant="text" onClick={() => setRedactTargetId(att.id)}>
                          Redact
                        </Button>
                      )}

                      <IconButton
                        size="small"
                        aria-label={`Download ${att.fileName}`}
                        onClick={() => {
                          const link = document.createElement('a');
                          link.href = att.dataUrl;
                          link.download = att.fileName;
                          link.click();
                        }}
                      >
                        <DownloadIcon fontSize="small" />
                      </IconButton>

                      <IconButton
                        size="small"
                        aria-label={`Remove ${att.fileName}`}
                        onClick={() => removeAttachment(att.id)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Paper>
                  ))}
                </Box>
              )}
            </Box>

            {/* Network Logs */}
            <FormControlLabel
              control={
                <Checkbox
                  checked={includeNetworkLogs}
                  onChange={(e) => setIncludeNetworkLogs(e.target.checked)}
                />
              }
              label="Include logs (network and console - sanitized)"
            />
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose} disabled={loading} size="small">
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={loading || !isFormValid}
            startIcon={loading && <CircularProgress size={16} />}
            size="small"
          >
            {loading ? 'Submitting...' : 'Submit Feedback'}
          </Button>
        </DialogActions>
      </Dialog>

      <RedactionModal
        open={!!redactTargetId}
        onClose={() => setRedactTargetId(null)}
        screenshot={redactTarget?.dataUrl ?? null}
        onSave={handleRedactionSave}
      />
    </>
  );
}
